import { pickerValueToDateKey } from "./picker-values.js";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
// src/modules/Scheduling/shared/availability/time-off.ts
dayjs.extend(utc);
dayjs.extend(timezone);
var TIME_OFF_TYPE_LABELS = {
	other: "Other",
	personal: "Personal",
	sick: "Sick",
	vacation: "Vacation"
};
var TIME_OFF_TYPE_OPTIONS = [
	"vacation",
	"sick",
	"personal",
	"other"
].map((value) => ({
	label: TIME_OFF_TYPE_LABELS[value],
	value
}));
/**
* Time off is stored as exact instants, the end not included. Each app shows
* them in its own zone: the scheduling app passes the region's, the others
* leave it out for the device's.
*/
function zoned(iso, timeZone) {
	return timeZone ? dayjs(iso).tz(timeZone) : dayjs(iso);
}
/**
* The zoned date as a plain calendar day. Day arithmetic and comparisons run
* on these: dayjs's subtract and startOf on a .tz() object land on the wrong
* instant across a DST change.
*/
var calendarDay = (d) => dayjs.utc(d.format("YYYY-MM-DD"));
var isMidnight = (d) => d.hour() === 0 && d.minute() === 0 && d.second() === 0;
/**
* Whole days in that zone: both ends on local midnights. The same rule the
* backend uses to show a range as dates in a push.
*/
function isWholeDayTimeOff(startTime, endTime, timeZone) {
	return isMidnight(zoned(startTime, timeZone)) && isMidnight(zoned(endTime, timeZone));
}
function dateLabel(d, currentYear) {
	return d.format(d.year() === currentYear ? "MMM D" : "MMM D, YYYY");
}
/**
* Whole days read as dates (`Sep 1–4`, `Sep 29–Oct 2`, `Sep 1`), the
* exclusive end becoming the last day off. Anything else reads with times
* (`Sep 1, 9:00 AM–1:00 PM`, `Sep 1, 9:00 AM–Sep 2, 5:00 PM`). The year only
* when it isn't this one.
*/
function formatTimeOffRange(startTime, endTime, timeZone) {
	const start = zoned(startTime, timeZone);
	const end = zoned(endTime, timeZone);
	const currentYear = (timeZone ? dayjs().tz(timeZone) : dayjs()).year();
	if (isWholeDayTimeOff(startTime, endTime, timeZone)) {
		const last = calendarDay(end).subtract(1, "day");
		const first = calendarDay(start);
		if (first.isSame(last, "day")) return dateLabel(first, currentYear);
		if (first.isSame(last, "year")) {
			const showYear = first.year() !== currentYear;
			const lastLabel = first.isSame(last, "month") ? last.format(showYear ? "D, YYYY" : "D") : last.format(showYear ? "MMM D, YYYY" : "MMM D");
			return `${first.format("MMM D")}–${lastLabel}`;
		}
		return `${dateLabel(first, currentYear)}–${dateLabel(last, currentYear)}`;
	}
	return `${`${dateLabel(start, currentYear)}, ${start.format("h:mm A")}`}–${calendarDay(start).isSame(calendarDay(end), "day") ? end.format("h:mm A") : `${dateLabel(end, currentYear)}, ${end.format("h:mm A")}`}`;
}
/** `3 days` for whole days, else the hours (`4 hours`, `1.5 hours`). */
function describeTimeOffLength(startTime, endTime, timeZone) {
	if (isWholeDayTimeOff(startTime, endTime, timeZone)) {
		const days = calendarDay(zoned(endTime, timeZone)).diff(calendarDay(zoned(startTime, timeZone)), "day");
		return days === 1 ? "1 day" : `${days} days`;
	}
	const hours = Math.round(dayjs(endTime).diff(dayjs(startTime), "minute") / 60 * 100) / 100;
	return hours === 1 ? "1 hour" : `${hours} hours`;
}
var nextDayKey = (dayKey) => dayjs.utc(dayKey).add(1, "day").format("YYYY-MM-DD");
/**
* Whole days off as the instants the server stores: midnight of the first day
* to midnight after the last, in that zone (the device's when left out).
*/
function wholeDaysToTimeOffRange(firstDayKey, lastDayKey, timeZone) {
	const midnight = (dayKey) => (timeZone ? dayjs.tz(dayKey, timeZone) : dayjs(dayKey)).toISOString();
	return {
		endTime: midnight(nextDayKey(lastDayKey)),
		startTime: midnight(firstDayKey)
	};
}
/**
* The requester may withdraw a pending request any time, and an approved one
* until it starts, unless a withdrawal already waits for a manager.
*/
function canWithdrawTimeOff(request, now = Date.now()) {
	if (request.status === "pending") return true;
	return request.status === "approved" && !request.withdrawalRequestedAt && now < Date.parse(request.startTime);
}
/**
* What the request form shows as days when editing: the first and last day of
* a whole-day request (the stored end is the midnight after the last day).
* Nothing for a part-day request, which shows as times instead.
*/
function timeOffEditDays(request) {
	if (!request || !isWholeDayTimeOff(request.startTime, request.endTime)) return {};
	return {
		end: dayjs(request.endTime).subtract(1, "day").toISOString(),
		start: request.startTime
	};
}
/**
* The instants the request form would save: whole days in the device's zone,
* or exact times. Undefined while it is incomplete or ends before it starts.
*/
function timeOffDraftRange({ days = {}, times = {}, withTimes }) {
	if (withTimes) {
		if (!times.start || !times.end) return void 0;
		const startTime = new Date(times.start).toISOString();
		const endTime = new Date(times.end).toISOString();
		return startTime < endTime ? {
			endTime,
			startTime
		} : void 0;
	}
	const first = pickerValueToDateKey(days.start);
	const last = pickerValueToDateKey(days.end);
	return first && last ? wholeDaysToTimeOffRange(first, last) : void 0;
}
export { TIME_OFF_TYPE_LABELS, TIME_OFF_TYPE_OPTIONS, canWithdrawTimeOff, describeTimeOffLength, formatTimeOffRange, isWholeDayTimeOff, timeOffDraftRange, timeOffEditDays, wholeDaysToTimeOffRange };

//# sourceMappingURL=time-off.js.map